<template>
  <div v-if="useWrapper" :class="`form-ant-datepicker-wrapper${wrapperClass ? ` ${wrapperClass}` : ''}`" data-type="datepicker">
    <a-date-picker
      v-if="mode === 'single'"
      v-bind="$attrs"
      v-model:value="singleDate"
      :placeholder="placeholder || 'Date'"
      :format="format"
      :picker="type"
      :allowClear="clearable"
      :inputReadOnly="inputReadOnly"
      @change="onSingleChange"
      :open="showPicker"
      @openChange="handleOpenChange"
    />
    <a-range-picker
      v-else-if="mode === 'range'"
      v-bind="$attrs"
      v-model:value="rangeDates"
      :placeholder="rangePlaceholder"
      :format="format"
      :picker="type"
      :allowClear="clearable"
      :inputReadOnly="inputReadOnly"
      @change="onRangeChange"
      ref="ant"
      :open="showPicker"
      @openChange="handleOpenChange"
    />
    <form-antd-multiple-date-picker
      v-else-if="mode === 'multiple'"
      v-bind="$attrs"
      v-model:value="multipleDates"
      :placeholder="placeholder || ''"
      :format="format"
      :allowClear="clearable"
      @change="onMultipleChange"
      ref="ant"
      :open="showPicker"
      @openChange="handleOpenChange"
    />
  </div>
  <template v-else>
    <a-date-picker
      v-if="mode === 'single'"
      v-bind="$attrs"
      v-model:value="singleDate"
      :placeholder="placeholder || 'Date'"
      :format="format"
      :picker="type"
      :allowClear="clearable"
      :inputReadOnly="inputReadOnly"
      @change="onSingleChange"
      :open="showPicker"
      @openChange="handleOpenChange"
    />
    <a-range-picker
      v-else-if="mode === 'range'"
      v-bind="$attrs"
      v-model:value="rangeDates"
      :placeholder="rangePlaceholder"
      :format="format"
      :picker="type"
      :allowClear="clearable"
      :inputReadOnly="inputReadOnly"
      @change="onRangeChange"
      ref="ant"
      :open="showPicker"
      @openChange="handleOpenChange"
    />
    <form-antd-multiple-date-picker
      v-else-if="mode === 'multiple'"
      v-bind="$attrs"
      v-model="multipleDates"
      :placeholder="placeholder || ''"
      :format="format"
      :allowClear="clearable"
      @change="onMultipleChange"
      ref="ant"
      :open="showPicker"
      @openChange="handleOpenChange"
    />
  </template>
</template>

<script>
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import FormAntdMultipleDatePicker from './FormAntdMultipleDatePicker.vue'

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  name: 'FormAntdDatePicker',
  inheritAttrs: false,
  components: {
    FormAntdMultipleDatePicker
  },
  props: {
    /**
     * Value bound via v-model.
     */
    modelValue: {
      type: [String, Array, Object],
      default: () => ''
    },
    /**
     * Change the type of date picker to display:
     * single - single date,
     * multiple - allows you to choose multiple dates in one picker.
     * range - allows you to choose a start and end date.
     */
    mode: {
      type: String,
      default: 'single'
    },
    /**
     * Format to use for dayjs
     */
    format: {
      type: String,
      default: 'M/D/YYYY'
    },
    /**
     * Placeholder.
     */
    placeholder: {
      type: [String, Array],
      default: ''
    },
    /**
     * Type to use for datepicker (date | week | month | quarter | year)
     */
    type: {
      type: String,
      default: 'date'
    },
    /**
     * Option to determine if selectedDate can be set to null.
     */
    clearable: {
      type: Boolean,
      default: false
    },
    /**
     * Set the readonly attribute of the input tag
     */
    inputReadOnly: {
      type: Boolean,
      default: false
    },
    /**
     * Timezone to use for dayjs
     */
    timezone: {
      type: String,
      default: dayjs.tz.guess()
    },
    /**
     * Use Wrapper.
     */
    useWrapper: {
      type: Boolean,
      default: false
    },
    /**
     * Additional classes for the wrapper.
     */
    wrapperClass: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      showPicker: false
    }
  },
  computed: {
    singleDate: {
      get() {
        if (this.modelValue && dayjs.tz(this.modelValue, this.timezone).isValid()) {
          return dayjs.tz(this.modelValue, this.timezone)
        }
        return this.modelValue || null
      },
      set(val) {
        this.$emit('update:modelValue', val ? dayjs.tz(val, this.timezone).toISOString() : null)
      }
    },
    rangeDates: {
      get() {
        if (this.modelValue?.start || this.modelValue?.end) {
          let start = this.modelValue?.start || ''
          let end = this.modelValue?.end || ''
          if (start && dayjs.tz(start, this.timezone).isValid()) {
            start = dayjs.tz(start, this.timezone)
          }
          if (end && dayjs.tz(end, this.timezone).isValid()) {
            end = dayjs.tz(end, this.timezone)
          }
          return [start, end]
        }
        return []
      },
      set(val) {
        let start = val?.[0] || ''
        let end = val?.[1] || ''
        if (start) {
          start = dayjs.tz(start, this.timezone).toISOString()
        }
        if (end) {
          end = dayjs.tz(end, this.timezone).toISOString()
        }
        this.$emit('update:modelValue', { start, end })
      }
    },
    multipleDates: {
      get() {
        let dates = []
        if (this.modelValue.length > 0) {
          this.modelValue?.forEach((item) => {
            if (item && dayjs.tz(item, this.timezone).isValid()) {
              dates.push(dayjs.tz(item, this.timezone))
            }
          })
        }
        return dates
      },
      set(val) {
        let dates = []
        if (val.length > 0) {
          val?.forEach((item) => {
            if (item) {
              dates.push(dayjs.tz(item, this.timezone).toISOString())
            }
          })
        }
        this.$emit('update:modelValue', dates)
      }
    },
    rangePlaceholder() {
      return Array.isArray(this.placeholder) ? this.placeholder : ['Start date', 'End date']
    }
  },
  methods: {
    onSingleChange(date) {
      this.singleDate = date
    },
    onRangeChange(dates) {
      this.rangeDates = dates
    },
    onMultipleChange(dates) {
      this.multipleDates = dates
    },
    handleOpenChange(status) {
      this.showPicker = status
    },
    openDatePicker() {
      this.showPicker = true
    },
    closeDatePicker() {
      this.showPicker = false
    }
  }
}
</script>

<style lang="postcss" scoped></style>
